/**
 * SCORM LMS API — Custom LMS Setup
 *
 * Articulate 360 (and every SCORM course) looks for a SCORM API object on
 * window.API (SCORM 1.2) or window.API_1484_11 (SCORM 2004) by traversing
 * up through parent frames. This module creates that LMS-side object and
 * attaches it to window BEFORE the iframe renders.
 *
 * pipwerks is a *content-side* helper (it calls the LMS, it is not the LMS).
 * Setting window.API = pipwerks.SCORM gives Articulate an object with no
 * LMSInitialize / LMSGetValue / etc. methods → "outside supported LMS" error.
 * We therefore implement the LMS interface ourselves here.
 *
 * Usage:
 *   initSCORM({ courseId, onCommit, onLocationChange })  ← before the iframe mounts
 *     onCommit(payload)      — fires on LMSCommit / LMSFinish (full state)
 *     onLocationChange(loc)  — fires on every lesson_location change (slide nav)
 *   teardownSCORM()          ← call on component unmount
 */

// ── helpers ───────────────────────────────────────────────────────────────────

function calcPercentage(status, scoreRaw, scoreMax) {
  if (status === "completed" || status === "passed") return 100;
  const raw = parseFloat(scoreRaw);
  const max = parseFloat(scoreMax) || 100;
  if (!isNaN(raw)) return Math.min(100, Math.round((raw / max) * 100));
  return 0;
}

// ── SCORM 1.2 LMS API factory ─────────────────────────────────────────────────

function createScorm12Api({ courseId, onCommit, onLocationChange }) {
  const store = {
    "cmi.core.lesson_status":   "not attempted",
    "cmi.core.lesson_location": "",
    "cmi.core.entry":           "ab-initio",
    "cmi.core.exit":            "",
    "cmi.core.score.raw":       "",
    "cmi.core.score.min":       "",
    "cmi.core.score.max":       "",
    "cmi.core.session_time":    "00:00:00",
    "cmi.core.total_time":      "00:00:00",
    "cmi.core.student_id":      "",
    "cmi.core.student_name":    "",
    "cmi.suspend_data":         "",
    "cmi.launch_data":          "",
    "cmi.comments":             "",
    "cmi.comments_from_lms":    "",
  };

  let errorCode = "0";

  function flush() {
    if (!onCommit) return;
    const status   = store["cmi.core.lesson_status"];
    const scoreRaw = store["cmi.core.score.raw"];
    const scoreMax = store["cmi.core.score.max"];
    onCommit({
      courseId,
      status,
      scoreRaw,
      suspendData:    store["cmi.suspend_data"],
      lessonLocation: store["cmi.core.lesson_location"],
      sessionTime:    store["cmi.core.session_time"],
      coursePercentage: calcPercentage(status, scoreRaw, scoreMax),
    });
  }

  return {
    LMSInitialize()      { errorCode = "0"; return "true"; },
    LMSGetValue(el)      { errorCode = "0"; return el in store ? store[el] : (errorCode = "201", ""); },
    LMSSetValue(el, val) {
      errorCode = "0";
      const prev = store[el];
      store[el] = val;
      // Fire onLocationChange when lesson_location changes between slides.
      // This catches navigation events that happen WITHOUT a subsequent LMSCommit.
      if (el === "cmi.core.lesson_location" && val !== prev && onLocationChange) {
        onLocationChange(val);
      }
      return "true";
    },
    LMSCommit() {
      errorCode = "0";
      if (onLocationChange) onLocationChange(store["cmi.core.lesson_location"]);
      flush();
      return "true";
    },
    LMSFinish() {
      errorCode = "0";
      store["cmi.core.exit"] = "normal";
      if (onLocationChange) onLocationChange(store["cmi.core.lesson_location"]);
      flush();
      return "true";
    },
    LMSGetLastError()    { return errorCode; },
    LMSGetErrorString(code) { return code === "0" ? "No error" : "Error"; },
    LMSGetDiagnostic(code)  { return code === "0" ? "No error" : "Error"; },
  };
}

// ── SCORM 2004 LMS API factory ────────────────────────────────────────────────

function createScorm2004Api({ courseId, onCommit, onLocationChange }) {
  const store = {
    "cmi.completion_status":  "unknown",
    "cmi.success_status":     "unknown",
    "cmi.location":           "",
    "cmi.entry":              "ab-initio",
    "cmi.exit":               "",
    "cmi.score.raw":          "",
    "cmi.score.min":          "",
    "cmi.score.max":          "",
    "cmi.score.scaled":       "",
    "cmi.session_time":       "PT0S",
    "cmi.total_time":         "PT0S",
    "cmi.learner_id":         "",
    "cmi.learner_name":       "",
    "cmi.suspend_data":       "",
    "cmi.launch_data":        "",
  };

  let errorCode = "0";

  function flush() {
    if (!onCommit) return;
    const status   = store["cmi.completion_status"];
    const scoreRaw = store["cmi.score.raw"];
    const scoreMax = store["cmi.score.max"];
    onCommit({
      courseId,
      status,
      scoreRaw,
      suspendData:    store["cmi.suspend_data"],
      lessonLocation: store["cmi.location"],
      sessionTime:    store["cmi.session_time"],
      coursePercentage: calcPercentage(status, scoreRaw, scoreMax),
    });
  }

  return {
    Initialize()      { errorCode = "0"; return "true"; },
    GetValue(el)      { errorCode = "0"; return el in store ? store[el] : (errorCode = "401", ""); },
    SetValue(el, val) {
      errorCode = "0";
      const prev = store[el];
      store[el] = val;
      // SCORM 2004 equivalent of lesson_location is cmi.location
      if (el === "cmi.location" && val !== prev && onLocationChange) {
        onLocationChange(val);
      }
      return "true";
    },
    Commit() {
      errorCode = "0";
      if (onLocationChange) onLocationChange(store["cmi.location"]);
      flush();
      return "true";
    },
    Terminate() {
      errorCode = "0";
      store["cmi.exit"] = "normal";
      if (onLocationChange) onLocationChange(store["cmi.location"]);
      flush();
      return "true";
    },
    GetLastError()    { return errorCode; },
    GetErrorString(code) { return code === "0" ? "No error" : "Error"; },
    GetDiagnostic(code)  { return code === "0" ? "No error" : "Error"; },
  };
}

// ── public API ────────────────────────────────────────────────────────────────

let _scorm12 = null;
let _scorm2004 = null;
let _session = 0; // increments on every init; teardown uses it as a guard

export function initSCORM({ courseId, onCommit, onLocationChange } = {}) {
  const session = ++_session;
  _scorm12   = createScorm12Api({ courseId, onCommit, onLocationChange });
  _scorm2004 = createScorm2004Api({ courseId, onCommit, onLocationChange });

  window.API        = _scorm12;   // SCORM 1.2
  window.API_1484_11 = _scorm2004; // SCORM 2004
  return session;
}

export function teardownSCORM(session) {
  // If a newer module has already called initSCORM, its window.API is live —
  // leave it alone so the new module's SCORM still works.
  if (session !== undefined && session !== _session) return;
  if (window.API        === _scorm12)   delete window.API;
  if (window.API_1484_11 === _scorm2004) delete window.API_1484_11;
  _scorm12   = null;
  _scorm2004 = null;
}
